/**
 * スタッフ出勤判定ユーティリティ
 *
 * priority の値で本日出勤/オフを判定する:
 *   - 1〜999: オフ
 *   - 1000〜1999: 本日出勤（= 1000 + オフ時の値）
 *
 * 他のコンポーネントからも利用可能な共通ユーティリティ。
 */

export const ON_DUTY_THRESHOLD = 1000;

/** 出勤中かどうかを判定 */
export const isOnDuty = (priority: number | null | undefined): boolean =>
  typeof priority === 'number' && priority >= ON_DUTY_THRESHOLD;

/** ベース優先度（オフ時の値 1〜999）を取得 */
export const basePriority = (priority: number): number =>
  ((priority % ON_DUTY_THRESHOLD) || 0);

/** 出勤状態にする（1000を加算） */
export const toOnDuty = (priority: number): number =>
  basePriority(priority) + ON_DUTY_THRESHOLD;

/** オフ状態にする（1000未満に正規化） */
export const toOffDuty = (priority: number): number =>
  basePriority(priority);

/**
 * 常に本日出勤扱いする固定スタッフ判定
 * - 共用アカウント: staff_login_id === 'staff' or staff_name === 'staff'/'スタッフ'
 * - スポット: staff_name === 'タイミー'
 *
 * これらは「本日勤務」管理モーダルにも、対応者選択モーダルにも表示しない（固定）。
 */
export const isFixedStaff = (s: any): boolean => {
  const name = String(s?.staff_name || '').trim();
  const loginId = String(s?.staff_login_id || '').trim();
  return loginId === 'staff' || name === 'staff' || name === 'スタッフ' || name === 'タイミー';
};

/**
 * 人間ではないシステムアカウントの判定（2026-08-02 追加）。
 *
 * ⚠️ 以前は `staff.id === 1`（那覇の「APIテスト用スタッフ」）だけを決め打ちで除外していた。
 *   スタッフ ID は**店舗ごとに独立した採番**のため、宮古では
 *   「APIテスト用スタッフ」が id=4、「外部イベント閲覧API専用アカウント」が id=3 となり、
 *   どちらも除外されず**実スタッフとして運転者・対応者の選択肢に出ていた**。
 *
 * ID ではなく、両店舗で共通の属性で判定する:
 *   - level 101 / 102 … 外部API専用アカウント（login: external-*）
 *   - login_id 'trailernchu' … APIテスト用スタッフ（両店舗とも同じ login_id）
 */
export const isSystemAccount = (s: any): boolean => {
  const level = Number(s?.level);
  if (Number.isFinite(level) && level >= 101) return true;
  const loginId = String(s?.staff_login_id || '').trim().toLowerCase();
  return loginId === 'trailernchu' || loginId.startsWith('external-');
};

/**
 * スポットスタッフ（タイミー）の判定（2026-08-02 追加）。
 * 実際の担当者名は予約メモの「貸出/返却運転者アサイン」から取り出す運用。
 *
 * ⚠️ 以前は `staff.id === 14`（那覇のタイミー）を決め打ちしていた。
 *   宮古のスタッフが14人に達すると無関係の人がメモ名に差し替えられるため、
 *   isFixedStaff と同じく名前で判定する。
 */
export const isSpotStaff = (s: any): boolean =>
  String(s?.staff_name || s?.name || '').trim() === 'タイミー';
